import React, { useMemo } from 'react';
import { Table, Typography, Tag, Button, Space, Tooltip } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { ReportColumn, ReportExecutionResult } from '../types';

const { Text } = Typography;

interface ReportDataTableProps {
  result: ReportExecutionResult;
  loading?: boolean;
  onPageChange?: (page: number, pageSize: number) => void;
  onSortChange?: (column: string, direction: 'ASC' | 'DESC') => void;
  onExportCsv?: () => void;
  onExportExcel?: () => void;
  onExportPdf?: () => void;
}

const ReportDataTable: React.FC<ReportDataTableProps> = ({
  result,
  loading = false,
  onPageChange,
  onSortChange,
  onExportCsv,
  onExportExcel,
  onExportPdf,
}) => {
  // Detect if this result has hierarchy (depth field present in any row)
  const hasHierarchy = useMemo(() => {
    return result.data?.some((row) => row['depth'] !== undefined && row['depth'] !== null) ?? false;
  }, [result.data]);

  const columns: ColumnsType<Record<string, unknown>> = useMemo(() => {
    if (!result.columns) return [];

    // Index of first visible STRING column — used for depth indentation
    const sortedCols = [...result.columns]
      .filter((col) => col.visible !== false)
      .sort((a, b) => (a.sequence || 999) - (b.sequence || 999));
    const firstStringColKey = hasHierarchy
      ? sortedCols.find((c) => c.type === 'STRING' || !c.type)?.key
      : undefined;

    const leafColumns = sortedCols.map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        width: col.width,
        align: col.align || (col.type === 'NUMBER' || col.type === 'CURRENCY' ? 'right' : 'left'),
        sorter: col.sortable,
        render: (value: unknown, record: Record<string, unknown>) => {
          if (hasHierarchy && col.key === firstStringColKey) {
            const depth = typeof record['depth'] === 'number' ? record['depth'] : 0;
            const indent = depth * 16;
            const isBold = depth <= 1;
            return (
              <span style={{ paddingLeft: indent, fontWeight: isBold ? 'bold' : 'normal', display: 'block' }}>
                {formatCellValue(value, col)}
              </span>
            );
          }
          return formatCellValue(value, col);
        },
        __headerGroup: col.headerGroup,
      }));

    const grouped: Array<any> = [];
    const groupOrder: string[] = [];
    const groupMap = new Map<string, any>();

    for (const col of leafColumns) {
      const groupName = (col as any).__headerGroup as string | undefined;
      if (!groupName) {
        const { __headerGroup, ...rest } = col as any;
        grouped.push(rest);
        continue;
      }

      if (!groupMap.has(groupName)) {
        groupOrder.push(groupName);
        groupMap.set(groupName, {
          title: groupName,
          key: `group_${groupName}`,
          children: [],
        });
      }

      const group = groupMap.get(groupName);
      const { __headerGroup, ...rest } = col as any;
      group.children.push(rest);
    }

    for (const groupName of groupOrder) {
      grouped.push(groupMap.get(groupName));
    }

    return grouped;
  }, [result.columns]);

  const formatCellValue = (value: unknown, column: ReportColumn): React.ReactNode => {
    if (value === null || value === undefined) {
      return <Text type="secondary">-</Text>;
    }

    switch (column.type) {
      case 'CURRENCY':
        return (
          <Text className={Number(value) < 0 ? 'text-red-600' : ''}>
            {formatCurrency(Number(value))}
          </Text>
        );

      case 'PERCENTAGE':
        return <Text>{Number(value).toFixed(2)}%</Text>;

      case 'NUMBER':
        return <Text>{formatNumber(Number(value))}</Text>;

      case 'DATE':
        return <Text>{formatDate(String(value))}</Text>;

      case 'DATETIME':
        return <Text>{formatDateTime(String(value))}</Text>;

      case 'BOOLEAN':
        return value ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="red">No</Tag>
        );

      case 'STATUS':
        return renderStatus(String(value));

      case 'LINK':
        if (column.linkTemplate) {
          const link = column.linkTemplate.replace(/\{(\w+)\}/g, (_, key) => String(value));
          return (
            <a href={link} className="text-blue-600 hover:underline">
              {String(value)}
            </a>
          );
        }
        return String(value);

      default:
        return String(value);
    }
  };

  const renderStatus = (status: string): React.ReactNode => {
    const statusColors: Record<string, string> = {
      active: 'green',
      inactive: 'gray',
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      completed: 'blue',
      cancelled: 'gray',
    };

    const color = statusColors[status.toLowerCase()] || 'default';
    return <Tag color={color}>{status}</Tag>;
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, unknown>,
    sorter: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[]
  ) => {
    if (onPageChange && pagination.current && pagination.pageSize) {
      onPageChange(pagination.current - 1, pagination.pageSize);
    }

    if (onSortChange && !Array.isArray(sorter) && sorter.column && sorter.order) {
      const direction = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      onSortChange(String(sorter.columnKey), direction);
    }
  };

  const summaryRow = useMemo(() => {
    if (!result.summary || !result.columns) return null;

    const hasSummary = result.columns.some((col) => col.aggregation);
    if (!hasSummary) return null;

    return () => (
      <Table.Summary fixed>
        <Table.Summary.Row className="bg-slate-50 font-semibold">
          {result.columns
            ?.filter((col) => col.visible !== false)
            .map((col, index) => (
              <Table.Summary.Cell key={col.key} index={index} align={col.align || 'left'}>
                {col.aggregation && result.summary ? (
                  <Text strong>
                    {formatSummaryValue(
                      result.summary[`${col.key}_${col.aggregation.toLowerCase()}`],
                      col
                    )}
                  </Text>
                ) : index === 0 ? (
                  <Text strong>Total</Text>
                ) : null}
              </Table.Summary.Cell>
            ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [result.summary, result.columns]);

  const formatSummaryValue = (value: unknown, column: ReportColumn): string => {
    if (value === null || value === undefined) return '-';

    if (column.type === 'CURRENCY') {
      return formatCurrency(Number(value));
    }
    if (column.type === 'NUMBER') {
      return formatNumber(Number(value));
    }
    return String(value);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100">
      {/* Header with export buttons */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div>
          <Text strong className="text-lg">
            {result.reportName}
          </Text>
          {result.pagination && (
            <Text type="secondary" className="ml-4">
              {result.pagination.totalRecords.toLocaleString()} records
            </Text>
          )}
        </div>

        <Space>
          {onExportCsv && (
            <Tooltip title="Export as CSV">
              <Button icon={<DownloadOutlined />} onClick={onExportCsv}>
                CSV
              </Button>
            </Tooltip>
          )}
          {onExportExcel && (
            <Tooltip title="Export as Excel">
              <Button icon={<FileExcelOutlined />} onClick={onExportExcel}>
                Excel
              </Button>
            </Tooltip>
          )}
          {onExportPdf && (
            <Tooltip title="Export as PDF">
              <Button icon={<FilePdfOutlined />} onClick={onExportPdf}>
                PDF
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Data Table */}
      <Table
        columns={columns}
        dataSource={result.data?.map((row, index) => ({ ...row, key: index }))}
        loading={loading}
        onChange={handleTableChange}
        rowClassName={(record) => {
          if (!hasHierarchy) return '';
          const depth = typeof record['depth'] === 'number' ? record['depth'] : -1;
          if (depth === 0) return 'bg-blue-100 font-bold';
          if (depth === 1) return 'bg-blue-50 font-semibold';
          return '';
        }}
        pagination={
          result.pagination
            ? {
                current: result.pagination.page + 1,
                pageSize: result.pagination.pageSize,
                total: result.pagination.totalRecords,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total.toLocaleString()} items`,
                pageSizeOptions: ['10', '25', '50', '100'],
              }
            : false
        }
        scroll={{ x: 'max-content' }}
        summary={summaryRow || undefined}
        size="middle"
      />

      {/* Footer with execution info */}
      <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
        <span>Generated at: {formatDateTime(result.generatedAt)}</span>
        <span>Execution time: {result.executionTimeMs}ms</span>
      </div>
    </div>
  );
};

// Utility functions
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
}

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export default ReportDataTable;
